<i18n>
{
  "fr": {
    "title": "Génération du manuel pour l'auditeur",
    "manual-date": "Date d'émission",
    "export": "Exporter",
    "projects-list-title": "Quels projets doivent être intégrés au manuel ?",
    "projects-list-empty": "Il n'y a présentement aucun projet actif dans votre entreprise.",
    "project-name-column": "Nom",
    "project-province-column": "Province",
    "project-status-column": "Statut"
  },
  "en": {
    "title": "Generate manual for the auditor",
    "manual-date": "Date of issue",
    "export": "Export",
    "projects-list-title": "Which projects should be included in the manual?",
    "projects-list-empty": "There are currently no active projects in your company.",
    "project-name-column": "Name",
    "project-province-column": "Province",
    "project-status-column": "Status"
  }
}
</i18n>

<template>
  <div>
    <ui-title>
      {{ $t('title') }}
      <template v-slot:description>
        <prismic-content :doctype="$consts.prismic.DOCUMENT_TYPE_MANUAL" v-slot="{ content }">
          <prismic-rich-text :field="content.page_introduction" />
        </prismic-content>
      </template>
    </ui-title>

    <div class="columns mt-4">
      <div class="column is-3">
        <!-- Date visée -->
        <s-field :label="$t('manual-date')" custom-class="is-size-7 is-uppercase">
          <s-locale-date icon="calendar-day" :placeholder="$t('manual-date')" v-model="manualDate"></s-locale-date>
        </s-field>
      </div>
      <div class="column" style="margin-top: auto">
        <s-loader :active="loading" />
        <b-button v-if="!loading" @click="generate" type="is-link" :disabled="!canExport">{{ $t('export') }}</b-button>
      </div>
    </div>

    <div class="card">
      <ui-box>
        <h2 class="title is-4 has-text-weight-bold is-marginless">
          {{ $t('projects-list-title') }}
        </h2>

        <b-table
          class="mt-16px"
          v-if="activeProjects"
          :data="activeProjects.items"
          checkable
          :checked-rows.sync="selectedProjects"
        >
          <template #empty>{{ $t('projects-list-empty') }}</template>

          <b-table-column v-slot="props" cell-class="has-text-weight-medium" field="name" :label="$t('project-name-column')">
            <router-link :to="{ name: $consts.urls.URL_PROJECT_COMPLIANCE, params: { projectId: props.row.id } }">
              {{ props.row.name }}
            </router-link>
          </b-table-column>
          <b-table-column v-slot="props" cell-class="has-text-weight-medium" field="name" :label="$t('project-province-column')">
            {{ getProvinceTranslation(props.row.provinceCode) }}
          </b-table-column>
          <b-table-column v-slot="props" field="status" :label="$t('project-status-column')">
            <project-status-badge :status="props.row.status" />
          </b-table-column>
        </b-table>
      </ui-box>
    </div>
  </div>
</template>
<script>
import ProvinceService from '@/services/province'
import SField from '@/components/form/field'
import SLocaleDate from '@/components/form/locale-date'
import ProjectStatusBadge from '@/components/projects/project-status-badge'
import UiBox from '@/components/ui/box'
import UiTitle from '@/components/ui/title'
import PrismicContent from '@/components/prismic/prismic-content'
import Breadcrumbs from '@/mixins/breadcrumbs'
import BusinessService from '@/services/business'
import i18n from '@/helpers/i18n'

export default {
  mixins: [Breadcrumbs],
  components: { SField, SLocaleDate, ProjectStatusBadge, UiBox, UiTitle, PrismicContent },
  data() {
    return { loading: false, manualDate: null, selectedProjects: [] }
  },
  computed: {
    breadcrumbs() {
      return [
        {
          label: this.$t('title')
        }
      ]
    },
    selectedIds() {
      return this.selectedProjects.map((x) => x.id)
    },
    canExport() {
      if (!this.manualDate) return false
      return true
    }
  },
  methods: {
    async generate() {
      this.loading = true

      try {
        var result = await this.$apollo.mutate({
          mutation: this.$options.mutation.GenerateManual,
          variables: {
            language: i18n.contentLanguage(),
            businessId: BusinessService.getCurrentBusiness().id,
            manualDate: this.manualDate,
            projectIds: this.selectedIds
          }
        })

        window.open(result.data.generateManualPdf.pdfUrl)
      } finally {
        this.loading = false
      }
    },
    getProvinceTranslation(provinceCode) {
      return ProvinceService.getProvinceTranslation(provinceCode)
    }
  },
  apollo: {
    activeProjects: {
      query() {
        return this.$options.query.ActiveProjects
      },
      variables() {
        return {
          businessId: BusinessService.getCurrentBusiness().id
        }
      }
    }
  }
}
</script>

<graphql>
query ActiveProjects($businessId: ID!) {
  activeProjects: searchProjects(businessId: $businessId, limit: 500, sort: PROVINCE_CODE, filter: { status: { eq: ACTIVE } }) {
    items {
      id
      name
      provinceCode
      status
    }
  }
}

mutation GenerateManual($language: ContentLanguage!, $businessId: ID!, $manualDate: DateTime!, $projectIds: [ID!]!) {
  generateManualPdf(input: { language: $language, businessId: $businessId, manualDate: $manualDate, projectIds: $projectIds }) {
    pdfUrl
  }
}
</graphql>